import type { LegalFooterContact, LegalFooterContactIcon } from '@kitup/brand';
import type { JSX } from 'react';

/**
 * Stroke icons, 24×24 grid (Lucide, ISC; TikTok from Tabler, MIT — Lucide has none).
 * One entry per `LegalFooterContactIcon`.
 */
const ICONS: Readonly<Record<LegalFooterContactIcon, JSX.Element>> = {
  phone: (
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  ),
  email: (
    <>
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </>
  ),
  telegram: (
    <>
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </>
  ),
  instagram: (
    <>
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <path d="M17.5 6.5h.01" />
    </>
  ),
  facebook: <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />,
  youtube: (
    <>
      <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
      <path d="m10 15 5-3-5-3z" />
    </>
  ),
  tiktok: (
    <path d="M21 7.917v4.034a9.948 9.948 0 0 1 -5 -1.951v4.5a6.5 6.5 0 1 1 -8 -6.326v4.326a2.5 2.5 0 1 0 4 2v-11.5h4.083a6.005 6.005 0 0 0 4.917 4.917" />
  ),
};

/**
 * The brand's contacts in the footer, each an icon plus its label. Holds
 * neither labels nor addresses: which contacts a brand has is brand data.
 */
export function ContactLinks({
  contacts,
}: {
  readonly contacts: readonly LegalFooterContact[];
}): JSX.Element {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2">
      {contacts.map((contact) => {
        const external = contact.href.startsWith('http');
        return (
          <a
            key={contact.href}
            href={contact.href}
            className="inline-flex items-center gap-2"
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {ICONS[contact.icon]}
            </svg>
            {contact.label}
          </a>
        );
      })}
    </div>
  );
}
